import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

const eur = (v) => Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const dIT = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "");

export const SollecitoSospesiDialog = ({ person, onClose }) => {
  const [mode, setMode] = useState("30gg");
  const [dataAl, setDataAl] = useState("");
  const [messaggio, setMessaggio] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!person || (mode === "data" && !dataAl)) { setPreview(null); return; }
    api.get("/sospesi/sollecito/preview", { params: { user_id: person.user_id, mode, data_al: mode === "data" ? dataAl : undefined } })
      .then((r) => setPreview(r.data)).catch((e) => { setPreview(null); toast.error(formatApiError(e)); });
  }, [person, mode, dataAl]);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/sospesi/sollecito", { user_id: person.user_id, mode, data_al: mode === "data" ? dataAl : undefined, messaggio });
      toast.success(`Sollecito inviato a ${r.data.email} (${r.data.count} sospesi)`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const ModeBtn = ({ id, label }) => (
    <button type="button" onClick={() => setMode(id)} data-testid={`sollecito-mode-${id}`}
      className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${mode === id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700 hover:border-slate-400"}`}>
      {label}
    </button>
  );

  return (
    <Dialog open={!!person} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sollecito-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-slate-700" /> Sollecito sospesi</DialogTitle>
          <DialogDescription>
            Email a <strong>{`${person?.nome || ""} ${person?.cognome || ""}`.trim()}</strong>
            {preview?.email ? <> ({preview.email})</> : null} con l&apos;elenco dei suoi sospesi.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <ModeBtn id="30gg" label="Oltre 30 giorni" />
            <ModeBtn id="data" label="Fino al…" />
          </div>
          {mode === "data" && (
            <div>
              <Label>Sospesi fino al (incluso)</Label>
              <Input type="date" value={dataAl} onChange={(e) => setDataAl(e.target.value)} data-testid="sollecito-data-al" />
            </div>
          )}
          <div>
            <Label>Messaggio (facoltativo)</Label>
            <Textarea rows={4} value={messaggio} onChange={(e) => setMessaggio(e.target.value)} placeholder="Es. Ti chiedo di recuperare questi premi entro fine settimana." data-testid="sollecito-messaggio" />
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="sollecito-preview">
            {!preview ? (
              <span className="text-slate-500">{mode === "data" && !dataAl ? "Scegli una data." : "Calcolo…"}</span>
            ) : (
              <>
                <div className="flex justify-between font-medium text-slate-900">
                  <span>{preview.count} sospes{preview.count === 1 ? "o" : "i"} inclus{preview.count === 1 ? "o" : "i"}</span>
                  <span className="font-mono-num" data-testid="sollecito-preview-total">{eur(preview.totale)}</span>
                </div>
                {preview.entries.length > 0 && (
                  <ul className="mt-2 max-h-40 overflow-y-auto divide-y divide-slate-200 text-xs text-slate-600">
                    {preview.entries.map((e, i) => (
                      <li key={i} className="flex justify-between py-1">
                        <span>{dIT(e.data)} · {e.contraente} <span className="font-mono">{e.numero_polizza}</span></span>
                        <span className="font-mono-num">{eur(e.netto)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {!preview.email && <div className="mt-2 text-xs text-red-700">L&apos;utente non ha un indirizzo email.</div>}
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={send} disabled={sending || !preview?.count || !preview?.email} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="sollecito-send">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Invio…" : "Invia sollecito"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
